/**
 * 续住明细
 */
import React, { useImperativeHandle, useRef, useState } from 'react';
import { Drawer, Button, message, Divider } from 'antd';
import intl from 'react-intl-universal';

import { fetchExtendedCheckinDetail } from '@/api/order';
import { cancelRenewalOrder } from '@/api/orderEdit';
import {
  OCC_TYPE_MAP,
  OCC_VALUE_MAP,
  OccType,
  SubOrderStatus,
  PriceChannel,
} from '@/config/constant';
import DateJS, { format2FullTimeStr } from '@/utils/date';
import usePortalConfirm from '@/components/PortalConfirm';
import { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import useCheckInEditor from '@/pages/reservation/orderEditor/checkInEditor';
import useCRSDetailDrawer from '@/pages/channel/channelOrder/centralReservationOrder/channelOrderDetail';
import DouyinChannelDetailDrawer from '@/pages/channel/tiktokDirect/components/DouyinChannelDetailDrawer';
import RoomInfoCard from '@/pages/order/detail/component/Room/components/RoomInfoCard';
import ReverseRollback, { RollbackDrawerRef } from '@/pages/reservation/reverseRollback';

import styles from './index.module.scss';

export interface RenewalRecordRef {
  openModal: (recNo: string) => void;
}

interface IRecord {
  id: number; // 编号
  resvNo: string; // 主单号
  recNo: string; // 子单号
  roomNo: string; // 房号
  sourceChannelType: PriceChannel;
  sourceChannelName: string; // 客源渠道
  priceChannel: PriceChannel; // 渠道类型
  priceStrategyName: string; // 计价策略
  channelOrderNo: string; // 渠道单号
  occType: OccType; // 入住类型
  checkinTime: number; // 入住时间
  occDuration: number; // 入住时长
  checkoutTime: number; // 退房时间
  expectedDepartureTime: number; // 预离时间
  status: SubOrderStatus; // 子订单状态
  directFlag: boolean; // 是否是续住直连预订
  nextNightAuditTime: number; // 入住后的夜审时间
}

interface IProps {
  innerRef: React.Ref<RenewalRecordRef>;
  onEnd?: (isModify: boolean) => void;
}

// 续住明细
const Index: React.FC<IProps> = (props) => {
  const [modalVisible, setVisible] = useState<boolean>(false);
  const [isLoading, setLoadingStatus] = useState<boolean>(false);
  const [tableDataSource, setTableData] = useState<IRecord[]>([]);
  const modifyRef = useRef(false);
  const tableWrapperRef = useRef<HTMLDivElement>();
  const confirmPortal = usePortalConfirm();
  const editorPortal = useCheckInEditor();
  const CRSDetailDrawer = useCRSDetailDrawer();
  const douyinRef = useRef<any>();
  const rollbackDrawerRef = useRef<RollbackDrawerRef>();

  const { innerRef, onEnd } = props;

  const closeModal = () => {
    setVisible(false);
    if (typeof onEnd === 'function') {
      onEnd(modifyRef.current);
    }
  };

  // 获取续住明细
  const getRenewalRecord = async (recNo) => {
    let listData = [];
    setLoadingStatus(true);
    try {
      const res = await fetchExtendedCheckinDetail({ recNo });
      listData = res.data || [];
    } catch (e) {
      //
    }
    setTableData(listData);
    setLoadingStatus(false);
    if (listData.length === 0) {
      // 续住记录为空
      closeModal();
    }
  };

  // 刷新记录
  const refreshRecordList = () => {
    modifyRef.current = true;
    const orderNo = tableDataSource.find((item) => item.recNo);
    if (orderNo) {
      getRenewalRecord(orderNo.recNo);
    }
  };

  useImperativeHandle(
    innerRef,
    () => ({
      openModal: (recNo) => {
        if (recNo) {
          setVisible(true); // 显示弹框
          setTableData([]); // 清空表格数据
          modifyRef.current = false; // 状态重置
          getRenewalRecord(recNo);
        }
      },
    }),
    [],
  );

  // 唤起入住修改抽屉
  const showOrderEditDrawer = (record: IRecord) => {
    editorPortal.showDrawer({
      renewalList: tableDataSource.map((item) => ({
        id: item.id,
        checked: item.id === record.id,
      })),
      onEnd: (isSuccess) => {
        if (isSuccess) {
          refreshRecordList();
        }
      },
    });
  };

  // 取消续住二次确认
  const showOrderCancelConfirm = (record: IRecord) => {
    confirmPortal.showDrawer({
      title: intl.get('reservation_cmp_extension_record_cancel_title'),
      content: intl.get('reservation_cmp_extension_record_cancel_content'),
      cancelText: intl.get('app_confirm_cancel_btn'),
      confirmText: intl.get('app_confirm_confirm_btn'),
      onConfirm: async () => {
        confirmPortal.closeDrawer();
        setLoadingStatus(true);
        try {
          await cancelRenewalOrder({ id: record.id });
          message.success(intl.get('reservation_cmp_extension_record_message_cancel_success'));
        } catch (e) {
          //
        }
        refreshRecordList();
      },
      onCancel: () => {
        confirmPortal.closeDrawer();
      },
    });
  };

  // 返还二次确认
  const showReverseConfirm = (record: IRecord) => {
    rollbackDrawerRef.current.openModal({ id: record.id });
  };

  // // 唤起钟点转日租抽屉
  // const showOrderConvertDrawer = (record) => {
  //   setVisible(false);
  //   console.log(record);
  // };

  // 中央预订详情
  const showCRSOrderDetail = (record: IRecord) => {
    const { channelOrderNo } = record;
    if (channelOrderNo) {
      if (record.sourceChannelType === PriceChannel.CRS) {
        CRSDetailDrawer.showDrawer({
          orderNo: channelOrderNo,
        });
      }
      if (record.sourceChannelType === PriceChannel.DY_ZL) {
        douyinRef.current.openDrawer({
          orderNo: channelOrderNo,
        });
      }
    }
  };

  const renderExtra = (_tableDataSource, record) => {
    const lastRecord = _tableDataSource[(_tableDataSource.length || 1) - 1];
    const isCheckInStatus = lastRecord ? lastRecord.status === SubOrderStatus.CheckIn : false;
    const hasCRSOrder = _tableDataSource.some(
      (item) => item.sourceChannelType === PriceChannel.CRS,
    );

    // 当前房间状态为入住中时可操作 或者 有CRS订单
    if (isCheckInStatus || hasCRSOrder) {
      const recordActions = [];
      if (isCheckInStatus) {
        recordActions.push(
          <>
            <Button onClick={() => showOrderEditDrawer(record)} key="edit">
              {intl.get('reservation_cmp_extension_record_table_edit_btn')}
            </Button>
            <Divider type="vertical" />
          </>,
        );
        // 最后一条续住记录
        if (record.id === lastRecord.id) {
          const { nextNightAuditTime, extensionType } = lastRecord;
          if (extensionType === 0 && DateJS().isBefore(nextNightAuditTime, 'second')) {
            // 不是直连续住 且 当前时间小于入住之后的夜审时间
            recordActions.push(
              <>
                <Button onClick={() => showOrderCancelConfirm(lastRecord)} key="cancel">
                  {intl.get('reservation_cmp_extension_record_table_cancel_btn')}
                </Button>
                <Divider type="vertical" />
              </>,
            );
          }
          if (extensionType === 1 && DateJS().isBefore(nextNightAuditTime, 'second')) {
            // 直连续住 且 当前时间小于入住之后的夜审时间
            recordActions.push(
              <>
                <Button onClick={() => showReverseConfirm(lastRecord)} key="reverse">
                  {intl.get('reservation_cmp_extension_record_table_rollback_btn')}
                </Button>
                <Divider type="vertical" />
              </>,
            );
          }
        }
      }

      if (
        record.sourceChannelType === PriceChannel.CRS ||
        record.sourceChannelType === PriceChannel.DY_ZL
      ) {
        recordActions.push(
          <>
            <Button onClick={() => showCRSOrderDetail(record)} key="crs-detail">
              {intl.get('reservation_cmp_extension_record_table_detail_btn')}
            </Button>
            <Divider type="vertical" />
          </>,
        );
      }
      return <div className={styles.recordActionWrapper}>{recordActions}</div>;
    }
    return null;
  };

  const renderKeyList = () => {
    const list: any = [
      {
        keyItemList: [
          { label: intl.get('app_reservation_channel_name'), key: 'sourceChannelName' },
          { label: intl.get('app_reservation_strategy_name'), key: 'priceStrategyName' },
          {
            label: intl.get('app_reservation_channel_no'),
            key: 'channelOrderNo',
            render: (val) => val || '—',
          },
        ],
        style: { width: '20%' },
      },
      {
        keyItemList: [
          {
            label: intl.get('app_reservation_occ_type'),
            key: 'occType',
            render: (val) => OCC_TYPE_MAP[val],
          },
          {
            label: intl.get('app_reservation_checkin_time'),
            key: 'checkinTime',
            render: (val) => format2FullTimeStr(val),
          },
          {
            label: intl.get('app_reservation_etd_time'),
            key: 'checkoutTime',
            render: (val) => format2FullTimeStr(val),
          },
          {
            label: intl.get('app_reservation_occ_duration'),
            key: 'occDuration',
            render: (val, record) => {
              return `${val}${OCC_VALUE_MAP[record.occType]}`;
            },
          },
        ],
        style: { width: '20%' },
      },
      {
        keyItemList: [
          {
            label: intl.get('app_reservation_reserve_remark'),
            key: 'remark',
            render: (val) => val || '—',
            style: { display: 'block' },
            labelStyle: { marginRight: 0, marginBottom: 8 },
          },
        ],
        style: { flex: 1 },
      },
      {
        keyItemList: [
          {
            label: intl.get('app_reservation_checkin_remark'),
            key: 'checkinRemark',
            render: (val) => val || '—',
            style: { display: 'block' },
            labelStyle: { marginRight: 0, marginBottom: 8 },
          },
        ],
        style: { flex: 1 },
      },
    ];
    return list;
  };

  return (
    <Drawer
      closable={false}
      destroyOnClose
      className={styles.renewalRecordDrawer}
      open={modalVisible}
    >
      <DouyinChannelDetailDrawer ref={douyinRef} />

      <DrawerHeader
        title={intl.get('reservation_cmp_extension_record_title')}
        onClose={closeModal}
      />
      <div className={styles.recordListWrapper}>
        <div className={styles.tableContainer} ref={tableWrapperRef}>
          {tableDataSource?.map((detail) => (
            <RoomInfoCard
              key={detail?.id}
              title={
                <>
                  <div className={styles.headerTitle}>
                    <div className={styles.headerItem}>
                      <span className={styles.label}>
                        {intl.get('reservation_cmp_extension_record_table_column_room')}
                      </span>
                      <span>{detail?.roomNo}</span>
                    </div>
                  </div>
                </>
              }
              extra={
                <>
                  <div className="flex-end">{renderExtra(tableDataSource, detail)}</div>
                </>
              }
              cardList={renderKeyList()}
              record={detail}
            />
          ))}
        </div>
      </div>
      <DrawerFooter>
        <Button type="primary" onClick={closeModal}>
          {intl.get('app_confirm_close_btn')}
        </Button>
      </DrawerFooter>
      <ReverseRollback innerRef={rollbackDrawerRef} onUpdate={refreshRecordList} />
    </Drawer>
  );
};

export default React.forwardRef<RenewalRecordRef, Pick<IProps, Exclude<keyof IProps, 'innerRef'>>>(
  (props, ref) => <Index {...props} innerRef={ref} />,
);
